import { ArticleType, Currency, FeePaymentType, formatMoneyAmount } from '@nemuru-eng/js-api-client';
import { InsuranceProvider, InsuranceType } from '@nemuru-eng/js-api-client/slim';
import { z } from 'zod';

import { SINGLE_UNIT_ARTICLE_TYPES } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useArticleTypes';
import { FormItemSchema } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/schemas/FormItemSchema';
import { VehicleAmountRange } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/vehicleAmountRange.map';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';

export const EditAutoPaymentMethodFinancingSchema = z.object({
  term: z.number().nullable(),
  instalmentAmount: z.number().nullable(),
  downPaymentAmount: z.number().nullable(),
  principalAmount: z.number().nullable(),
  discountAmount: z.number().nullable(),
  annualInterest: z.number().nullable(),
  insurance: z.nativeEnum(InsuranceType).nullable(),
  insuranceProvider: z.nativeEnum(InsuranceProvider).nullable(),
  setupFeeAmount: z.number().nullable(),
  setupFeeType: z.nativeEnum(FeePaymentType).nullable(),
});

export const EditAutoPaymentMethodFormSchema = (
  t: GetTextIdTranslationType,
  vehicleAmountRange: VehicleAmountRange,
  currency: Currency,
) =>
  z.object({
    item: FormItemSchema(t)
      .refine((data) => !(SINGLE_UNIT_ARTICLE_TYPES.includes(data.type) && Number(data.units) > 1), {
        message: t('MORE_THAN_ONE_UNIT_ERROR'),
        path: ['units'],
      })
      .refine((data) => !(data.type === ArticleType.AUTOMOBILE && !(data.isNewVehicle || data.registrationDate)), {
        message: t('REQUIRED_FIELD'),
        path: ['registrationDate'],
      })
      .refine(
        (data) => !(data.type === ArticleType.AUTOMOBILE && Number(data.price) < vehicleAmountRange.min),
        {
          message: t('AUTO_AMOUNT_OUT_OF_RANGE').replace(
            '$v1',
            formatMoneyAmount({ amount: vehicleAmountRange.min, currency }),
          ),
          path: ['price'],
        },
      ),
    financing: EditAutoPaymentMethodFinancingSchema,
  });

export type EditAutoPaymentMethodFormFields = z.infer<ReturnType<typeof EditAutoPaymentMethodFormSchema>>;
export type EditAutoPaymentMethodFinancingFields = z.infer<typeof EditAutoPaymentMethodFinancingSchema>;
